import {
  MilestoneCreateModal,
  MilestoneTemplateModal,
  MilestoneTemplateTable,
} from "@/containers/ProjectTemplate";
import { MilestoneNameEditModal } from "@/containers/ProjectTemplate/components/MilestoneNameEditModal";
import { updateMilestoneRecursively } from "@/containers/ProjectTemplate/components/helpers";
import { IMilestoneTemplate } from "@/interfaces";
import { Button, Row, Space, Switch } from "antd";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { ModalNameType } from "../types";

interface IProps {
  milestoneTemplates: IMilestoneTemplate[];
  setMilestoneTemplates: Dispatch<SetStateAction<IMilestoneTemplate[]>>;
  refetchProjectTemplate: () => void;
  onToggleKeyMilestone?: (milestoneId: string, checked: boolean) => void;
}

interface IMilestoneTemplateEditorModal {
  modalOpen: boolean;
  milestoneTemplate?: IMilestoneTemplate;
}

export const MilestoneTemplateEditor = (props: IProps) => {
  const intl = useIntl();

  const [modalProps, setModalProps] = useState<{
    [key: string]: IMilestoneTemplateEditorModal;
  }>({
    template: {
      modalOpen: false,
    },
    name: {
      modalOpen: false,
    },
    addTop: {
      modalOpen: false,
    },
    addChild: {
      modalOpen: false,
    },
    addBefore: {
      modalOpen: false,
    },
    addAfter: {
      modalOpen: false,
    },
    create: {
      modalOpen: false,
    },
  });
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [expandAll, setExpandAll] = useState<boolean>(false);
  const [showActive, setShowActive] = useState<boolean>(false);
  const [milestoneToExpand, setMilestoneToExpand] = useState<string>("");

  const getExpandableMilestones = (): string[] =>
    props.milestoneTemplates
      .filter(
        ({ childMilestones }): IMilestoneTemplate[] | undefined =>
          childMilestones
      )
      .map(({ id }): string => id);

  const handleExpandAll = (expandAll: boolean): void => {
    expandAll
      ? setExpandedRows(getExpandableMilestones())
      : setExpandedRows([]);

    setExpandAll(expandAll);
  };

  const toggleModal = (
    modalName: ModalNameType,
    milestoneTemplate?: IMilestoneTemplate
  ): void => {
    setModalProps({
      ...modalProps,
      [modalName]: {
        modalOpen: !modalProps[modalName].modalOpen,
        milestoneTemplate,
      },
    });
  };

  const saveMilestoneTemplate = (
    updateMilestoneTemplate: IMilestoneTemplate
  ): void => {
    const updatedMilestoneList = updateMilestoneRecursively(
      updateMilestoneTemplate,
      props.milestoneTemplates
    );

    props.setMilestoneTemplates(updatedMilestoneList);
  };

  const saveMilestoneTemplates = (
    updateMilestoneTemplates: IMilestoneTemplate[]
  ): void => {
    props.setMilestoneTemplates(updateMilestoneTemplates);
  };

  useEffect(() => {
    if (milestoneToExpand !== "") {
      const newChildRow = document.getElementById(
        milestoneToExpand
      ) as HTMLElement;
      if (newChildRow) {
        newChildRow.scrollIntoView(true);
      }
    }
  }, [milestoneToExpand]);
  return (
    <>
      <Row className="w-full pb-1" align="middle" justify="space-between">
        <div>
          <Button
            data-test-id="milestone-templates-add-top-milestone"
            onClick={(): void => toggleModal("create")}
          >
            {intl.formatMessage({
              id: "projectMilestoneTable.createMilestone",
            })}
          </Button>
        </div>

        <Space direction="horizontal" size="small">
          <Switch
            data-test-id="milestone-template-toggle-active"
            checkedChildren={intl.formatMessage({
              id: "milestoneTemplates.showActive",
            })}
            unCheckedChildren={intl.formatMessage({
              id: "milestoneTemplates.showAll",
            })}
            checked={showActive}
            onChange={(): void => setShowActive(!showActive)}
          />
          <Switch
            data-test-id="milestone-template-toggle-all-visible"
            checkedChildren={intl.formatMessage({
              id: "milestoneTemplates.showChildren",
            })}
            unCheckedChildren={intl.formatMessage({
              id: "milestoneTemplates.hideChildren",
            })}
            checked={expandAll}
            onChange={(): void => {
              handleExpandAll(!expandAll);
            }}
          />
        </Space>
      </Row>
      <MilestoneTemplateTable
        expandedRows={expandedRows}
        showActive={showActive}
        milestoneTemplates={props.milestoneTemplates}
        setExpandedRows={setExpandedRows}
        setMilestoneTemplates={props.setMilestoneTemplates}
        toggleModal={toggleModal}
        onToggleKeyMilestone={props.onToggleKeyMilestone}
      />
      <MilestoneTemplateModal
        modalOpen={modalProps.template.modalOpen}
        milestoneTemplate={
          modalProps.template.milestoneTemplate || ({} as IMilestoneTemplate)
        }
        saveMilestoneTemplate={(milestoneTemplate): void =>
          saveMilestoneTemplate(milestoneTemplate)
        }
        onClose={(): void => toggleModal("template")}
      />
      <MilestoneNameEditModal
        key={modalProps.name.milestoneTemplate?.id}
        modalOpen={modalProps.name.modalOpen}
        milestoneTemplate={modalProps.name.milestoneTemplate}
        onOk={(milestoneTemplate: IMilestoneTemplate): void => {
          props.setMilestoneTemplates((value): IMilestoneTemplate[] =>
            updateMilestoneRecursively(milestoneTemplate, value)
          );
          toggleModal("name");
        }}
        onClose={(): void => toggleModal("name")}
      />
      <MilestoneCreateModal
        modalOpen={modalProps.create.modalOpen}
        targetMilestone={{} as IMilestoneTemplate}
        milestoneTemplates={props.milestoneTemplates}
        saveMilestoneTemplates={saveMilestoneTemplates}
        onClose={(): void => toggleModal("create")}
        target={"top"}
      />
      {modalProps.addChild.milestoneTemplate && (
        <MilestoneCreateModal
          modalOpen={modalProps.addChild.modalOpen}
          targetMilestone={modalProps.addChild.milestoneTemplate}
          milestoneTemplates={props.milestoneTemplates}
          saveMilestoneTemplates={saveMilestoneTemplates}
          onClose={(): void => toggleModal("addChild")}
          target={"child"}
          expandedRows={expandedRows}
          setExpandedRows={setExpandedRows}
          setMilestoneToExpand={setMilestoneToExpand}
        />
      )}
      {modalProps.addBefore.milestoneTemplate && (
        <MilestoneCreateModal
          modalOpen={modalProps.addBefore.modalOpen}
          targetMilestone={
            modalProps.addBefore.milestoneTemplate || ({} as IMilestoneTemplate)
          }
          milestoneTemplates={props.milestoneTemplates}
          saveMilestoneTemplates={saveMilestoneTemplates}
          onClose={(): void => toggleModal("addBefore")}
          target={"before"}
        />
      )}
      {modalProps.addAfter.milestoneTemplate && (
        <MilestoneCreateModal
          modalOpen={modalProps.addAfter.modalOpen}
          targetMilestone={
            modalProps.addAfter.milestoneTemplate || ({} as IMilestoneTemplate)
          }
          milestoneTemplates={props.milestoneTemplates}
          saveMilestoneTemplates={saveMilestoneTemplates}
          onClose={(): void => toggleModal("addAfter")}
          target={"after"}
        />
      )}
    </>
  );
};
